import { useEffect, useMemo, useState } from 'react'
import { Plus, Edit, Folder, FolderTree, Power, PowerOff, Search, ChevronDown, ChevronRight, RefreshCw, X, Trash2, Loader2 } from 'lucide-react'
import { contentApi, uploadApi, type ContentCategory } from '../../lib/api'

type ShowModal =
  | null
  | { mode: 'create'; level: 1 | 2; parent_id?: string; parent_name?: string }
  | { mode: 'edit'; id: string; level: 1 | 2; parent_id?: string; name: string; code: string; sort_order: number; icon_url?: string | null; description?: string }

const ContentCategories = () => {
  const [tree, setTree] = useState<ContentCategory[]>([])
  const [contentCounts, setContentCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState<ShowModal>(null)
  const [keyword, setKeyword] = useState('')
  const [activeOnly, setActiveOnly] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const load = async () => {
    setLoading(true); setError('')
    try {
      // 用 list 接口（含 inactive），方便展示停用分类
      const [list, stats] = await Promise.all([
        contentApi.adminListCategoriesAll(),
        contentApi.adminStats().catch(() => null),
      ])
      setTree(list)
      // 用统计接口的 by_category 反查每分类内容数
      const cnt: Record<string, number> = {}
      if (stats && (stats as any).by_category) {
        // 简化：以一级分类名为 key 计数；二级可在编辑后单独统计
        Object.entries((stats as any).by_category).forEach(([k, v]) => { cnt[k] = (cnt[k] || 0) + (v as number) })
      }
      setContentCounts(cnt)
    } catch (e: any) {
      setError(e.message || '加载失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const toggleActive = async (c: ContentCategory) => {
    const action = c.is_active === 1 ? '停用' : '启用'
    if (!confirm(`确认${action}「${c.name}」？${action}后该分类${c.is_active === 1 ? '不能用于新建内容' : '可重新使用'}。`)) return
    try {
      await contentApi.adminUpdateCategory(c.id, { is_active: c.is_active === 1 ? 0 : 1 })
      load()
    } catch (e: any) { alert(e.message || `${action}失败`) }
  }

  const handleDelete = async (c: ContentCategory) => {
    if (!confirm(`确认删除「${c.name}」？\n\n注意：删除后不可恢复。若该分类下有子分类或内容，将无法删除。`)) return
    try {
      await contentApi.adminDeleteCategory(c.id)
      load()
    } catch (e: any) { alert(e.message || '删除失败') }
  }

  const submit = async (data: any) => {
    if (!showModal) return
    try {
      if (showModal.mode === 'create') {
        await contentApi.adminCreateCategory(data)
      } else {
        await contentApi.adminUpdateCategory(showModal.id, data)
      }
      setShowModal(null)
      load()
    } catch (e: any) { alert(e.message || '保存失败') }
  }

  const level1 = useMemo(() => tree.filter(c => c.level === 1), [tree])
  const level2Map = useMemo(() => {
    const m = new Map<string, ContentCategory[]>()
    for (const c of tree.filter(c => c.level === 2)) {
      if (!c.parent_id) continue
      if (!m.has(c.parent_id)) m.set(c.parent_id, [])
      m.get(c.parent_id)!.push(c)
    }
    return m
  }, [tree])

  const kw = keyword.trim().toLowerCase()
  const filterCat = (c: ContentCategory) => {
    if (activeOnly && c.is_active !== 1) return false
    if (kw && !c.name.toLowerCase().includes(kw) && !(c.code || '').toLowerCase().includes(kw)) return false
    return true
  }
  const visibleLevel1 = level1.filter(filterCat)
  const visibleLevel2ByParent = new Map<string, ContentCategory[]>()
  for (const p of visibleLevel1) {
    const kids = (level2Map.get(p.id) || []).filter(filterCat)
    visibleLevel2ByParent.set(p.id, kids)
  }

  const toggleCollapse = (id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">分类管理</h1>
          <p className="text-sm text-gray-500 mt-1">维护两级分类树 · 共 {level1.length} 个一级 / {tree.filter(c => c.level === 2).length} 个二级</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load()} className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> 刷新
          </button>
          <button onClick={() => setShowModal({ mode: 'create', level: 1 })} className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700">
            <Plus className="w-4 h-4" /> 新建一级分类
          </button>
        </div>
      </div>

      {/* 筛选行 */}
      <div className="bg-white rounded border p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="搜索分类名称或编码" className="w-full border rounded pl-9 pr-3 py-2 text-sm" />
        </div>
        <label className="flex items-center gap-1 text-sm text-gray-600">
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} className="rounded" />
          仅看启用
        </label>
        {(keyword || activeOnly) && (
          <button onClick={() => { setKeyword(''); setActiveOnly(false) }} className="text-xs text-gray-500 hover:text-gray-700">
            <X className="w-3 h-3 inline" /> 清空筛选
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}

      {loading && <div className="text-gray-400 text-sm">加载中…</div>}

      {visibleLevel1.length === 0 && !loading && (
        <div className="bg-white rounded border p-10 text-center text-gray-400 text-sm">
          {keyword || activeOnly ? '无匹配的分类' : '暂无分类，点击右上角新建一级分类开始'}
        </div>
      )}

      <div className="space-y-3">
        {visibleLevel1.map(parent => {
          const children = visibleLevel2ByParent.get(parent.id) || []
          const isCollapsed = collapsed.has(parent.id)
          return (
            <div key={parent.id} className="bg-white border rounded">
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <button onClick={() => toggleCollapse(parent.id)} className="text-gray-400 hover:text-gray-700" title={isCollapsed ? '展开子分类' : '收起子分类'}>
                    {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {parent.icon_url ? (
                    <img src={parent.icon_url} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0" />
                  ) : (
                    <Folder className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  )}
                  <span className="font-medium truncate">{parent.name}</span>
                  <span className="text-xs text-gray-400">({parent.code})</span>
                  {parent.is_active === 0 && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 rounded">已停用</span>}
                  <span className="text-xs text-gray-400 ml-2">· {children.length} 个子分类</span>
                </div>
                <div className="flex items-center gap-2 text-xs flex-shrink-0">
                  <button onClick={() => setShowModal({ mode: 'edit', id: parent.id, level: 1, name: parent.name, code: parent.code, sort_order: parent.sort_order, icon_url: parent.icon_url, description: parent.description })} className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                    <Edit className="w-3.5 h-3.5" /> 编辑
                  </button>
                  <button onClick={() => toggleActive(parent)} className={`${parent.is_active === 1 ? 'text-yellow-600' : 'text-green-600'} hover:underline inline-flex items-center gap-0.5`}>
                    {parent.is_active === 1 ? <><Power className="w-3.5 h-3.5" /> 停用</> : <><PowerOff className="w-3.5 h-3.5" /> 启用</>}
                  </button>
                  <button
                    onClick={() => handleDelete(parent)}
                    className="text-red-600 hover:underline inline-flex items-center gap-0.5"
                    title={children.length > 0 ? '有子分类，需先删除子分类' : '删除分类'}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> 删除
                  </button>
                  <button
                    onClick={() => setShowModal({ mode: 'create', level: 2, parent_id: parent.id, parent_name: parent.name })}
                    className="text-green-600 hover:underline inline-flex items-center gap-0.5"
                    disabled={parent.is_active === 0}
                    title={parent.is_active === 0 ? '一级分类已停用，不能新建子分类' : '新建子分类'}
                  >
                    <Plus className="w-3.5 h-3.5" /> 新建子分类
                  </button>
                </div>
              </div>
              {!isCollapsed && children.length > 0 && (
                <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-3 gap-2 border-t pt-3">
                  {children.map(c => (
                    <div key={c.id} className={`flex items-center justify-between rounded px-2 py-1.5 text-xs ${c.is_active === 0 ? 'bg-gray-100 text-gray-400' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        {c.icon_url ? (
                          <img src={c.icon_url} alt="" className="w-4 h-4 rounded object-cover flex-shrink-0" />
                        ) : (
                          <FolderTree className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        )}
                        <span className="truncate">{c.name}</span>
                        <span className="text-gray-300">({c.code})</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => setShowModal({ mode: 'edit', id: c.id, level: 2, parent_id: c.parent_id, name: c.name, code: c.code, sort_order: c.sort_order, icon_url: c.icon_url, description: c.description })} className="text-blue-600 hover:underline">编辑</button>
                        <button onClick={() => toggleActive(c)} className={`${c.is_active === 1 ? 'text-yellow-600' : 'text-green-600'} hover:underline`}>
                          {c.is_active === 1 ? '停用' : '启用'}
                        </button>
                        <button onClick={() => handleDelete(c)} className="text-red-600 hover:underline" title="删除分类">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!isCollapsed && children.length === 0 && (
                <div className="px-3 pb-3 text-xs text-gray-400 border-t pt-3">暂无子分类</div>
              )}
            </div>
          )
        })}
      </div>

      {showModal && (
        <CategoryModal
          state={showModal}
          parentOptions={level1.filter(p => p.is_active === 1)}
          onCancel={() => setShowModal(null)}
          onSubmit={submit}
        />
      )}
    </div>
  )
}

const CategoryModal = ({ state, parentOptions, onCancel, onSubmit }: { state: ShowModal; parentOptions: ContentCategory[]; onCancel: () => void; onSubmit: (data: any) => void }) => {
  const isCreate = state && 'mode' in state && state.mode === 'create'
  const level = state ? ('level' in state ? state.level : 1) : 1
  const initial = state && 'mode' in state && state.mode === 'edit' ? state : null

  const [code, setCode] = useState(initial?.code || '')
  const [name, setName] = useState(initial?.name || '')
  const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? 0)
  const [iconUrl, setIconUrl] = useState(initial?.icon_url || '')
  const [iconUploading, setIconUploading] = useState(false)
  const [description, setDescription] = useState(initial?.description || '')
  // 父分类：新建二级时用 state.parent_id；编辑二级时用 initial.parent_id
  const initParentId = isCreate && state && 'parent_id' in state ? (state.parent_id || '') : initial?.parent_id || ''
  const [parentId, setParentId] = useState<string>(initParentId)

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      alert('图标大小不能超过 2MB')
      return
    }
    e.target.value = ''
    setIconUploading(true)
    try {
      const url: string = await uploadApi.uploadImage(file)
      setIconUrl(url)
    } catch (err: any) {
      alert('上传失败：' + (err?.message || '未知错误'))
    } finally {
      setIconUploading(false)
    }
  }

  const onOk = () => {
    if (!name.trim()) return alert('名称必填')
    const data: any = { name: name.trim(), sort_order: Number(sortOrder) || 0, icon_url: iconUrl || null, description: description || undefined }
    if (isCreate) {
      data.code = `${parentId || 'cat'}-${(code || '').trim() || Date.now()}`
      if (level === 2) {
        if (!parentId) return alert('二级分类必须选择父分类')
        data.parent_id = parentId
      }
    }
    onSubmit(data)
  }

  const parentName = parentOptions.find(p => p.id === parentId)?.name

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-md p-5">
        <h3 className="text-lg font-medium mb-3">
          {isCreate ? `新建${level === 1 ? '一级' : '二级'}分类` : `编辑${level === 1 ? '一级' : '二级'}分类`}
          {level === 2 && parentName && <span className="text-sm text-gray-500 ml-2">（父：{parentName}）</span>}
        </h3>
        {level === 1 && isCreate && (
          <div className="text-xs text-gray-500 mb-3">提示：一级分类编码建议英文/拼音；留空自动生成 cat-时间戳。</div>
        )}
        {level === 2 && (
          <label className="block mb-3 text-sm">
            <span className="text-gray-700 mb-1 inline-block">父分类 <span className="text-red-500">*</span></span>
            <select value={parentId} onChange={e => setParentId(e.target.value)} className="w-full border rounded px-3 py-2">
              <option value="">请选择</option>
              {parentOptions.map(p => <option key={p.id} value={p.id}>{p.name}{p.is_active === 0 ? '（已停用）' : ''}</option>)}
            </select>
          </label>
        )}
        {isCreate && (
          <label className="block mb-3 text-sm">
            <span className="text-gray-700 mb-1 inline-block">编码 <span className="text-xs text-gray-400">（选填，自动拼接）</span></span>
            <input value={code} onChange={e => setCode(e.target.value)} placeholder={level === 2 ? '如 hypertension' : '如 chronic-disease'} className="w-full border rounded px-3 py-2" />
          </label>
        )}
        <label className="block mb-3 text-sm">
          <span className="text-gray-700 mb-1 inline-block">名称 <span className="text-red-500">*</span></span>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="如 高血压" className="w-full border rounded px-3 py-2" />
        </label>

        {/* 图标上传（选填） */}
        <div className="mb-3 text-sm">
          <span className="text-gray-700 mb-1 inline-block">分类图标 <span className="text-xs text-gray-400">（选填，用户端金刚区展示）</span></span>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {iconUploading ? (
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              ) : iconUrl ? (
                <img src={iconUrl} alt="分类图标" className="w-full h-full object-cover" />
              ) : (
                <Folder className="w-5 h-5 text-gray-300" />
              )}
            </div>
            <label className={`px-3 py-1.5 border border-gray-200 rounded text-xs text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors flex-shrink-0 ${iconUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input type="file" accept="image/*" className="hidden" onChange={handleIconUpload} disabled={iconUploading} />
              {iconUploading ? '上传中…' : '选择图片'}
            </label>
            {iconUrl && (
              <button type="button" onClick={() => setIconUrl('')} className="text-xs text-red-500 hover:underline flex-shrink-0">清除</button>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1">建议 200×200 方形图，≤2MB；不传则用户端按分类名自动配色</p>
        </div>

        <label className="block mb-3 text-sm">
          <span className="text-gray-700 mb-1 inline-block">排序 <span className="text-xs text-gray-400">（数字大=靠前）</span></span>
          <input type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} className="w-full border rounded px-3 py-2" />
        </label>
        <label className="block mb-4 text-sm">
          <span className="text-gray-700 mb-1 inline-block">说明</span>
          <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="选填，前后台展示用" className="w-full border rounded px-3 py-2" />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm border rounded">取消</button>
          <button onClick={onOk} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700">保存</button>
        </div>
      </div>
    </div>
  )
}

export default ContentCategories